import { useEffect } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

// Task 3.9 Round 3 (DECISIONS.md D-29) — GSAP + Lenis adoption, superseding
// D-27's framer-motion-only stance. Rashid instructed adopting the industry-
// standard scroll stack (evidence: TRIONN.com, a verified Awwwards WOTD, ships
// on exactly GSAP + ScrollTrigger + Lenis).
//
// This module is the single place that:
//   1. registers GSAP's ScrollTrigger plugin (once),
//   2. drives inertial smooth scroll site-wide with Lenis, and
//   3. syncs Lenis → ScrollTrigger so every scrubbed animation stays in lockstep
//      with the smoothed scroll position (per the research brief's §9.0 pattern).
//
// Reduced motion: Lenis is NOT initialised at all — the browser's native scroll
// is left untouched, and every scroll-bound animation elsewhere renders its
// static fallback. The framer-motion primitives that remain (Reveal / SplitText /
// scrollfx) already read the same OS preference, so the whole system opts out
// together.

// Register the plugin once at module load. ScrollTrigger is safe to register in a
// non-DOM (test/SSR) context — it no-ops until a trigger is created.
gsap.registerPlugin(ScrollTrigger);

// Module-level singleton so imperative callers (cold-load hash deep-links) can
// route a jump through Lenis instead of fighting its RAF loop with a native
// scrollIntoView. Null whenever Lenis isn't running (reduced motion, or before
// mount).
let lenisInstance = null;

export function getLenis() {
  return lenisInstance;
}

// Scroll to an element by id. Used by the pages' cold-load hash deep-link effects
// (/#start, /solutions#industries, …). Routes through Lenis when it's active so
// its internal target position stays in sync; falls back to the native jump when
// Lenis isn't running (reduced motion / not yet mounted).
export function scrollToId(id, { immediate = true } = {}) {
  if (!id) return;
  const el = typeof document !== 'undefined' && document.getElementById(id);
  if (!el) return;
  if (lenisInstance) {
    lenisInstance.scrollTo(el, { immediate });
  } else {
    el.scrollIntoView();
  }
}

// <SmoothScroll /> — mount once at the app shell. Owns the Lenis lifecycle and its
// GSAP ticker/ScrollTrigger sync, and tears everything down cleanly (important:
// it toggles with the reduced-motion preference, which can flip at runtime).
export function SmoothScroll() {
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) return undefined; // native scroll; no smoothing, no Lenis
    if (typeof window === 'undefined') return undefined;

    const lenis = new Lenis({
      duration: 1.05,
      // ease-out expo — a quick start that settles softly, the "premium" decel
      // Rashid asked for in Round 2 rather than a linear, mechanical scroll.
      easing: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisInstance = lenis;

    // Keep ScrollTrigger's notion of scroll position tied to Lenis's smoothed one.
    lenis.on('scroll', ScrollTrigger.update);

    // Drive Lenis from GSAP's ticker (one RAF loop for the whole page) and disable
    // lag smoothing so scrub stays glued to the pointer even after a frame drop.
    const onTick = (time) => lenis.raf(time * 1000); // gsap ticker time is seconds
    gsap.ticker.add(onTick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(onTick);
      lenis.destroy();
      if (lenisInstance === lenis) lenisInstance = null;
    };
  }, [reduced]);

  return null;
}
